import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { map, Observable, of, switchMap } from 'rxjs';

import { ConfirmationDialog, ConfirmationDialogData } from '../confirmation-dialog.component';
import { InputDialog, InputDialogData } from '../input-dialog/input-dialog.component';

export interface DoubleConfirmationDialogData {
  confirmation: ConfirmationDialogData;
  /** Input dialog config. `required`, `validationValue` and `validationLabel` are controlled by the top-level fields. */
  input: Omit<InputDialogData, 'required' | 'validationValue' | 'validationLabel'>;
  /** The value the user must type exactly to enable the confirm button. */
  validationValue: string;
  /** Translation key shown as an error when the typed value does not match. */
  validationLabel?: string;
}

/**
 * Sequences a {@link ConfirmationDialog} and an {@link InputDialog} with value validation.
 * Returns an Observable that emits `true` when both steps are confirmed, or `false` otherwise.
 *
 * #### Usage
 *
 * ```ts
 * this.doubleConfirmationService.open({
 *   confirmation: {
 *     title: 'ConfirmTitle',
 *     description: 'ConfirmMessage',
 *   },
 *   input: {
 *     title: 'InputTitle',
 *     description: 'InputMessage',
 *     placeholder: 'InputPlaceholder',
 *   },
 *   validationValue: 'ValidationValue',
 *   validationLabel: 'ValidationLabel',
 * }).subscribe(confirmed => {
 *   if (confirmed) {
 *     // proceed
 *   }
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DoubleConfirmationService {
  private readonly dialog = inject(MatDialog);

  open(data: DoubleConfirmationDialogData): Observable<boolean> {
    return this.dialog
      .open(ConfirmationDialog, { data: data.confirmation })
      .afterClosed()
      .pipe(
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }
          return this.dialog
            .open(InputDialog, {
              data: {
                ...data.input,
                required: true,
                validationValue: data.validationValue,
                validationLabel: data.validationLabel,
              } as InputDialogData,
            })
            .afterClosed()
            .pipe(map((value: string | undefined) => value !== undefined));
        }),
      );
  }
}
